import { useDebouncedValue } from '@mantine/hooks'
import {
  keepPreviousData,
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
  useSuspenseQuery,
} from '@tanstack/react-query'
import { useLocation, useNavigate } from '@tanstack/react-router'
import axios from 'axios'
import { QueryError } from 'errors'
import { MRT_ColumnFiltersState, MRT_PaginationState, MRT_SortingState } from 'mantine-react-table'
import { Filterable } from 'types/index'
import {
  Category,
  EditProduct,
  Manufacturer,
  NewManufacturer,
  NewProduct,
  NewProductWithoutVariant,
  Product,
  ProductSymbols,
  ProductWithVariants,
  Variant,
} from 'types/products'
import { useIsProductModifiedStore } from '../routes/_auth/product.$productId.{-$section}'

type SearchProductParams = {
  symbol: string
  get?: number
  skip?: number
}

export function useSearchProducts({ symbol, get, skip }: SearchProductParams) {
  return useQuery<Filterable<Product>, QueryError>({
    queryKey: ['products', symbol, get, skip],
    queryFn: async () => {
      const { data } = await axios.get('/products', { params: { symbol, get: get || 6, skip: skip || 0 } })
      return data
    },
    placeholderData: keepPreviousData,
    staleTime: 1000 * 15,
  })
}

export function useSearchProductsSymbols({ symbol }: { symbol: string }) {
  return useQuery<Array<ProductSymbols>>({
    queryKey: ['productsSymbols', symbol],
    queryFn: async () => {
      const { data } = await axios.get('/products/symbols', { params: { symbol } })
      return data
    },
    placeholderData: keepPreviousData,
    staleTime: 1000 * 15,
  })
}

export function useProduct(symbol: string) {
  return useQuery<Array<Product>, QueryError, Product | undefined>({
    queryKey: ['product', symbol],
    queryFn: async () => {
      if (symbol === '') {
        return []
      }
      return (await axios.get('/products', { params: { symbol } })).data
    },
    select: (data) => data[0],
  })
}

export function useProductWithVariants(productId?: string) {
  return useSuspenseQuery(productWithVariantsQueryOptions(productId))
}

export function useProductWithVariantsWithoutSuspense(productId?: string) {
  return useQuery(productWithVariantsQueryOptions(productId))
}

export function productWithVariantsQueryOptions(productId?: string) {
  return queryOptions({
    queryKey: ['productWithVariants', productId],
    queryFn: async () => {
      if (!productId) return undefined
      return (await axios.get<ProductWithVariants>(`/products/${productId}`)).data
    },
    enabled: !!productId,
    staleTime: 1000 * 15,
  })
}

export function useProductsOnPlace(placeId: string | null) {
  return useQuery<Array<ProductWithVariants>, QueryError>({
    queryKey: ['productsOnPlace', placeId],
    queryFn: async () => {
      if (!placeId) return []
      return (await axios.get(`/products/place/${placeId}`)).data
    },
    enabled: !!placeId,
  })
}

interface Params {
  category: Category
  columnFilters: MRT_ColumnFiltersState
  sorting: MRT_SortingState
  pagination: MRT_PaginationState
}

export function variantsQuery<T extends Variant>({ category, columnFilters, sorting, pagination }: Params) {
  const columnFiltersWithCategory = [
    ...columnFilters.flatMap((filter) => {
      if (filter.id === 'place' && typeof filter.value === 'object' && filter.value !== null) {
        const place: Array<number | string> = filter.value as Array<number | string>
        const keys = ['rack', 'column', 'shelf'] as const

        return place.flatMap(
          (v, index) => (v !== '' ? [{ id: `place.${keys[index]}`, value: v.toString() }] : []) // return [] instead of undefined to skip
        )
      }

      return [filter]
    }),
    { id: 'category', value: category },
  ]

  const fetchUrl = new URL('/variants', window.location.origin)
  fetchUrl.searchParams.set('skip', `${pagination.pageIndex * pagination.pageSize}`)
  fetchUrl.searchParams.set('get', `${pagination.pageSize}`)
  fetchUrl.searchParams.set('filters', JSON.stringify(columnFiltersWithCategory))
  fetchUrl.searchParams.set('sorting', JSON.stringify(sorting ?? []))

  return queryOptions<Filterable<T & { product: { id: string; symbol: string } }>>({
    queryKey: ['variants', fetchUrl],
    queryFn: async () => {
      return (await axios.get(`${fetchUrl.pathname}${fetchUrl.search}`)).data
    },
    placeholderData: keepPreviousData,
    staleTime: 15_000,
  })
}

export function useVariants<T extends Variant>(params: Params) {
  return useSuspenseQuery(variantsQuery<T>(params))
}

export function useAddProduct<T>({ withoutVariant = false }: { withoutVariant?: boolean } = {}) {
  const isAddPage = useLocation().pathname === '/add'
  const navigate = useNavigate({ from: 'add' })
  const queryClient = useQueryClient()

  return useMutation<ProductWithVariants, QueryError, NewProduct<T> | NewProductWithoutVariant>({
    mutationKey: ['addProduct'],
    mutationFn: async (product: NewProduct<T> | NewProductWithoutVariant) => {
      let payload = { ...product }
      if ('alternateSymbol' in payload) {
        payload = { ...payload, alternateSymbol: payload.alternateSymbol || null }
      }
      return (await axios.post('/products', { ...payload, withoutVariant })).data
    },
    onSuccess: (addedProduct) => {
      queryClient.setQueryData(['productWithVariants', addedProduct.id], addedProduct)
      if (isAddPage) {
        navigate({ to: `/product/${addedProduct.id}` })
      }
    },
    meta: { successMessage: 'Produkt został dodany.', closeModal: true },
  })
}

export function useEditProduct() {
  const setIsModified = useIsProductModifiedStore((state) => state.setIsModified)
  const queryClient = useQueryClient()

  return useMutation<ProductWithVariants, QueryError, EditProduct>({
    mutationKey: ['editProduct'],
    mutationFn: async (product: EditProduct) => {
      setIsModified(true)
      const payload = { ...product }
      if ('alternateSymbol' in payload) {
        payload.alternateSymbol = payload.alternateSymbol || null
      }

      return (await axios.put(`/products/${product.id}`, payload)).data
    },
    onSuccess: (editedProduct) => {
      queryClient.setQueryData(['productWithVariants', editedProduct.id], editedProduct)
    },
    meta: { successMessage: 'Produkt został zaktualizowany.', closeModal: true },
  })
}

export function useDeleteProduct() {
  const setIsModified = useIsProductModifiedStore((state) => state.setIsModified)
  const queryClient = useQueryClient()
  const navigate = useNavigate()

  return useMutation<void, QueryError, Product>({
    mutationKey: ['deleteProduct'],
    mutationFn: async (product: Product) => {
      setIsModified(true)
      await axios.delete(`/products/${product.id}`)
    },
    onSuccess: (_, { id: productId }) => {
      queryClient.invalidateQueries({ queryKey: ['products'] })
      queryClient.invalidateQueries({ queryKey: ['productWithVariants', productId] })
      queryClient.setQueryData(['productsOnPlace'], (prev: Array<ProductWithVariants>) => {
        if (!prev) return prev
        return prev.filter((product) => product.id !== productId)
      })
      navigate({ to: '/' })
    },
    meta: { successMessage: 'Produkt został usunięty.', closeModal: true },
  })
}

export const manufacturersQueryOptions = queryOptions<Array<Manufacturer>, QueryError>({
  queryKey: ['manufacturers'],
  queryFn: async () => {
    return (await axios.get('/manufacturers')).data
  },
  staleTime: 60_000,
})

export function useManufacturers() {
  return useSuspenseQuery(manufacturersQueryOptions)
}

export function useUpdateManufacturer() {
  const queryClient = useQueryClient()

  return useMutation<Manufacturer, QueryError, Manufacturer>({
    mutationKey: ['updateManufacturer'],
    mutationFn: async (manufacturer: Manufacturer) => {
      return (await axios.put(`/manufacturers/${manufacturer.id}`, manufacturer)).data
    },
    onSuccess: (updatedManufacturer) => {
      queryClient.setQueryData<Array<Manufacturer>>(['manufacturers'], (prev) => {
        if (!prev) return prev
        return prev.map((manufacturer) =>
          manufacturer.id === updatedManufacturer.id ? updatedManufacturer : manufacturer
        )
      })
    },
    meta: { successMessage: 'Producent został zaktualizowany.', closeModal: true },
  })
}

export function useAddManufacturer() {
  const queryClient = useQueryClient()

  return useMutation<Manufacturer, QueryError, NewManufacturer>({
    mutationKey: ['addManufacturer'],
    mutationFn: async (manufacturer: NewManufacturer) => {
      return (await axios.post('/manufacturers', manufacturer)).data
    },
    onSuccess: (addedManufacturer) => {
      queryClient.setQueryData<Array<Manufacturer>>(['manufacturers'], (prev) => {
        if (!prev) return prev
        return [...prev, addedManufacturer]
      })
    },
    meta: { successMessage: 'Producent został dodany.', closeModal: true },
  })
}

export function useDeleteManufacturer() {
  const queryClient = useQueryClient()

  return useMutation<void, QueryError, Manufacturer>({
    mutationKey: ['deleteManufacturer'],
    mutationFn: async (manufacturer: Manufacturer) => {
      await axios.delete(`/manufacturers/${manufacturer.id}`)
    },
    onSuccess: (_, deletedManufacturer) => {
      queryClient.setQueryData<Array<Manufacturer>>(['manufacturers'], (prev) => {
        if (!prev) return prev
        return prev.filter((manufacturer) => manufacturer.id !== deletedManufacturer.id)
      })
    },
    meta: { successMessage: 'Producent został usunięty.', closeModal: true },
  })
}

export function useCheckIfProductExists({ symbol, alternateSymbol }: { symbol?: string; alternateSymbol?: string }) {
  const [debouncedSymbol] = useDebouncedValue(symbol, 500)
  const type = alternateSymbol ? 'alternateSymbol' : 'symbol'

  return useQuery<{ exists: boolean }, QueryError, boolean>({
    queryKey: ['checkIfSymbolExists', debouncedSymbol],
    queryFn: async () => {
      const { data } = await axios.get<{ exists: boolean }>(`/products/exists?${type}=${debouncedSymbol}`)
      return data
    },
    select: (data) => data.exists,
    enabled: (!symbol || !alternateSymbol) && debouncedSymbol !== '',
  })
}
